import React, { Suspense, lazy } from "react";
import { Route } from "react-router-dom";
import ProtectedRoute from "../utils/ProtectedRoute";
import PrivateRoutes from "../PrivateRoutes";
import ClientProjectRoute from "../utils/ClientProjectRoute";
import AdminProjectRoute from "../utils/AdminProjectRoute";
import { NotificationProvider } from "../pages/UserManagement/NotificationContext";
import RoleBasedLayout from "../components/shared/RoleBasedLayout";
import RoleBasedDashboard from "../components/shared/RoleBasedDashboard";
import Loader from "../components/shared/Loader";
import RouteErrorBoundary from "../components/shared/RouteErrorBoundary";
import Homepage from "../pages/Homepage";
import Contact from "../pages/Contact";
import About from "../pages/About";
import Login from "../pages/Login/Login";
import SignUpContractorPage from "../pages/SignUp/ContractorSignUp";
import SetPassword from "../pages/SetPassword/SetPassword";
import ForgetPassword from "../pages/ForgotPassword/ForgetPassword";
import NotFoundPage from "../pages/NotFoundPage";
import AccessDenied from "../pages/AccessDenied";
import CustomerSecondaryLayout from "../components/shared/CustomerSecondaryLayout";
const Property = lazy(() => import("../pages/CustomerFlow/Property"));
const BuildingOverviews = lazy(() => import("../pages/CustomerFlow/BuildingOverviews"));
const BuildingElements = lazy(() => import("../pages/CustomerFlow/BuildingElements"));
const FinanceView = lazy(() => import("../pages/CustomerFlow/FinanceView"));
const MaintainenceView = lazy(() => import("../pages/CustomerFlow/MaintainenceView"));
const Document = lazy(() => import("../pages/CustomerFlow/Document"));
const Help = lazy(() => import("../pages/CustomerFlow/Help"));

const Dashboard = lazy(() => import("../pages/Clients/Dashboard"));
const ClientProject = lazy(() => import("../pages/Clients/ClientProject"));
const ProjectDetailsPage = lazy(() => import("../pages/Clients/ProjectDetailsPage"));
const ProjectControlCenter = lazy(() => import("../pages/Clients/ProjectControlCenter"));
const ManageBids = lazy(() => import("../pages/Clients/ManageBids"));
const ClientBidDetails = lazy(() => import("../pages/Clients/ClientBidDetails"));
const ClientTaskDetails = lazy(() => import("../pages/Clients/ClientTaskDetails"));
const DamageManagement = lazy(() => import("../pages/Clients/DamageManagement/DamageManagement"));
const SingleTaskManagement = lazy(
  () => import("../pages/Clients/SingleTaskManagement/SingleTaskManagement"),
);
const Comparison = lazy(() => import("../pages/Clients/Comparison"));
const OngoingTenderDetails = lazy(() => import("../pages/Clients/OngoingTenderDetails"));

const Marketplace = lazy(() => import("../pages/Contractor/Marketplace"));
const ManageTenders = lazy(() => import("../pages/Contractor/ManageTenders"));
const TenderTasksView = lazy(() => import("../pages/Contractor/TenderTasksView"));
const BidDetails = lazy(() => import("../components/Contractor/BidDetails"));
const AssignContractor = lazy(() => import("../components/Contractor/AssignContractor"));
const ContractorDetails = lazy(() => import("../components/Contractor/ContractorDetails"));
const TenderWorkspace = lazy(() => import("../components/Contractor/TenderWorkspace"));
const TenderListPage = lazy(() => import("../components/Contractor/TenderListPage"));
const ConversionRateAnalysis = lazy(
  () => import("../components/Contractor/ConversionRateAnalysis"),
);
const ClientRatingAnalysis = lazy(() => import("../components/Contractor/ClientRatingAnalysis"));
const ContractorProfile = lazy(() => import("../components/Contractor/Profile/ContractorProfile"));

const Projects = lazy(() => import("../pages/Admin/Projects"));
const ClientRequest = lazy(() => import("../pages/Admin/ClientRequest"));
const UserManagement = lazy(() => import("../pages/UserManagement/Page"));
const Roles = lazy(() => import("../pages/Roles/Page"));
const ObjectItems = lazy(() => import("../pages/ObjectItems/Page"));

const Details = lazy(() => import("../pages/Details/Details"));
const PartialDetails = lazy(() => import("../pages/Details/PartialDetails"));
const DetailedOverview = lazy(() => import("../pages/Details/DetailedOverview"));
const BuildingDetailsPage = lazy(() => import("../pages/Details/BuildingDetailsPage"));
const AnnotationTool = lazy(() => import("../pages/AnnotationTool/AnnotationTool"));

const Reports = lazy(() => import("../pages/Reports/Page"));
const ReportsMain = lazy(() => import("../pages/Reports/components/ReportsMain"));
const InformationStep = lazy(() => import("../pages/Reports/Stepper/Information"));
const BuildingOverview = lazy(() => import("../pages/Reports/Stepper/BuildingOverview"));
const SubElements = lazy(() => import("../pages/Reports/Stepper/SubElements"));
const FinancialOverview = lazy(() => import("../pages/Reports/Stepper/FinancialOverview"));
const Maintenance = lazy(() => import("../pages/Reports/Stepper/Maintenance/Page"));
const DamagedImage = lazy(() => import("../pages/Clients/DamagedImage"));
const SharedDamageViewer = lazy(() => import("../pages/shared/SharedDamageViewer"));

const TENDER_LIST_ROUTES = [
  {
    path: "contractor/tenders/pending",
    statusKey: "pending",
    entityKey: "tenders",
    titleKey: "pendingTenders",
  },
  {
    path: "contractor/tenders/active",
    statusKey: "active",
    entityKey: "tenders",
    titleKey: "activeTenders",
  },
  {
    path: "contractor/tenders/completed",
    statusKey: "completed",
    entityKey: "tenders",
    titleKey: "completedTenders",
  },
  {
    path: "contractor/tasks/pending",
    statusKey: "pending",
    entityKey: "tasks",
    titleKey: "pendingTasks",
  },
  {
    path: "contractor/tasks/active",
    statusKey: "active",
    entityKey: "tasks",
    titleKey: "activeTasks",
  },
  {
    path: "contractor/tasks/completed",
    statusKey: "completed",
    entityKey: "tasks",
    titleKey: "completedTasks",
  },
];

export function renderTenderListRoutes(t) {
  return TENDER_LIST_ROUTES.map(({ path, statusKey, entityKey, titleKey }) => (
    <Route
      key={path}
      path={path}
      element={
        <Suspense fallback={<Loader />}>
          <TenderListPage
            status={t(`tenderList.${statusKey}`)}
            entity={t(`tenderList.${entityKey}`)}
            title={t(`tenderList.${titleKey}`)}
          />
        </Suspense>
      }
    />
  ));
}

export function PublicRoutes() {
  return (
    <>
      <Route path="/" element={<Homepage />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/onboard/contractor" element={<SignUpContractorPage />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/forget-password" element={<ForgetPassword />} />
      <Route
        path="/shared/damage/:token"
        element={
          <Suspense fallback={<Loader />}>
            <SharedDamageViewer />
          </Suspense>
        }
      />
    </>
  );
}

export function CustomerBuildingRoutes() {
  return (
    <Route element={<CustomerSecondaryLayout />}>
      <Route
        path="building/information"
        element={
          <Suspense fallback={<Loader />}>
            <Property />
          </Suspense>
        }
      />
      <Route
        path="buildings/overview"
        element={
          <Suspense fallback={<Loader />}>
            <BuildingOverviews />
          </Suspense>
        }
      />
      <Route
        path="building/overview/building-part/:id"
        element={
          <Suspense fallback={<Loader />}>
            <BuildingElements />
          </Suspense>
        }
      />
      <Route
        path="building/finance"
        element={
          <Suspense fallback={<Loader />}>
            <FinanceView />
          </Suspense>
        }
      />
      <Route
        path="building/maintainence"
        element={
          <Suspense fallback={<Loader />}>
            <MaintainenceView />
          </Suspense>
        }
      />
    </Route>
  );
}

export function ReportsRoutes() {
  return (
    <Route
      path="reports"
      element={
        <Suspense fallback={<Loader />}>
          <Reports />
        </Suspense>
      }
    >
      <Route
        index
        element={
          <Suspense fallback={<Loader />}>
            <ReportsMain />
          </Suspense>
        }
      />
      <Route
        path="information"
        element={
          <Suspense fallback={<Loader />}>
            <InformationStep />
          </Suspense>
        }
      />
      <Route
        path="building-overview"
        element={
          <Suspense fallback={<Loader />}>
            <BuildingOverview />
          </Suspense>
        }
      >
        <Route
          path="element/:id"
          element={
            <Suspense fallback={<Loader />}>
              <SubElements />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="financial-overview"
        element={
          <Suspense fallback={<Loader />}>
            <FinancialOverview />
          </Suspense>
        }
      />
      <Route
        path="maintenance"
        element={
          <Suspense fallback={<Loader />}>
            <Maintenance />
          </Suspense>
        }
      />
    </Route>
  );
}

export function RoleBasedRoutes(t) {
  return (
    <Route element={<RoleBasedLayout />}>
      <Route index element={<RoleBasedDashboard />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route
        path="damaged-image"
        element={
          <Suspense fallback={<Loader />}>
            <DamagedImage />
          </Suspense>
        }
      />

      <Route
        path="projects"
        element={
          <Suspense fallback={<Loader />}>
            <ClientProjectRoute>
              <ClientProject />
            </ClientProjectRoute>
          </Suspense>
        }
      />
      <Route
        path="projects/:id"
        element={
          <Suspense fallback={<Loader />}>
            <ProjectDetailsPage />
          </Suspense>
        }
      />
      <Route
        path="projects/:id/control-center"
        element={
          <Suspense fallback={<Loader />}>
            <ProjectControlCenter />
          </Suspense>
        }
      />
      <Route
        path="project"
        element={
          <Suspense fallback={<Loader />}>
            <AdminProjectRoute>
              <Projects />
            </AdminProjectRoute>
          </Suspense>
        }
      />

      <Route
        path="manageBids"
        element={
          <Suspense fallback={<Loader />}>
            <ManageBids />
          </Suspense>
        }
      />
      <Route
        path="bid-details/:id"
        element={
          <Suspense fallback={<Loader />}>
            <ClientBidDetails />
          </Suspense>
        }
      />
      <Route
        path="comparison/:bidId"
        element={
          <Suspense fallback={<Loader />}>
            <Comparison />
          </Suspense>
        }
      />

      <Route
        path="damage-management"
        element={
          <Suspense fallback={<Loader />}>
            <DamageManagement />
          </Suspense>
        }
      />
      <Route
        path="damage-management/:id"
        element={
          <Suspense fallback={<Loader />}>
            <SingleTaskManagement />
          </Suspense>
        }
      />
      <Route
        path="task-details/:id"
        element={
          <Suspense fallback={<Loader />}>
            <ClientTaskDetails />
          </Suspense>
        }
      />

      <Route
        path="assign-contractor"
        element={
          <Suspense fallback={<Loader />}>
            <AssignContractor />
          </Suspense>
        }
      />
      <Route
        path="ongoing-tender-details/:tender_id"
        element={
          <Suspense fallback={<Loader />}>
            <OngoingTenderDetails />
          </Suspense>
        }
      />
      <Route
        path="contractor/ongoing/tasks/:tenderId"
        element={
          <Suspense fallback={<Loader />}>
            <TenderWorkspace />
          </Suspense>
        }
      />

      {renderTenderListRoutes(t)}

      <Route
        path="contractor/conversion"
        element={
          <Suspense fallback={<Loader />}>
            <ConversionRateAnalysis />
          </Suspense>
        }
      />
      <Route
        path="contractor/rating"
        element={
          <Suspense fallback={<Loader />}>
            <ClientRatingAnalysis />
          </Suspense>
        }
      />
      <Route
        path="contractor/profile"
        element={
          <Suspense fallback={<Loader />}>
            <ContractorProfile />
          </Suspense>
        }
      />
      <Route
        path="contractor/:id"
        element={
          <Suspense fallback={<Loader />}>
            <ContractorDetails />
          </Suspense>
        }
      />

      <Route
        path="marketplace"
        element={
          <Suspense fallback={<Loader />}>
            <Marketplace />
          </Suspense>
        }
      />
      <Route
        path="marketplace/bid/:id"
        element={
          <Suspense fallback={<Loader />}>
            <BidDetails />
          </Suspense>
        }
      />
      <Route
        path="manage-tender"
        element={
          <Suspense fallback={<Loader />}>
            <ManageTenders />
          </Suspense>
        }
      />
      <Route
        path="tender-tasks/:tenderId"
        element={
          <Suspense fallback={<Loader />}>
            <TenderTasksView />
          </Suspense>
        }
      />

      <Route
        path="document"
        element={
          <Suspense fallback={<Loader />}>
            <Document />
          </Suspense>
        }
      />
      <Route
        path="help"
        element={
          <Suspense fallback={<Loader />}>
            <Help />
          </Suspense>
        }
      />
      <Route
        path="contact"
        element={
          <Suspense fallback={<Loader />}>
            <Contact />
          </Suspense>
        }
      />

      <Route
        path="user-management"
        element={
          <Suspense fallback={<Loader />}>
            <UserManagement />
          </Suspense>
        }
      />
      <Route
        path="roles"
        element={
          <Suspense fallback={<Loader />}>
            <Roles />
          </Suspense>
        }
      />
      <Route
        path="object-items"
        element={
          <Suspense fallback={<Loader />}>
            <ObjectItems />
          </Suspense>
        }
      />
      <Route
        path="client-request"
        element={
          <Suspense fallback={<Loader />}>
            <AdminProjectRoute>
              <ClientRequest />
            </AdminProjectRoute>
          </Suspense>
        }
      />
      <Route
        path="details"
        element={
          <Suspense fallback={<Loader />}>
            <Details />
          </Suspense>
        }
      />
      <Route
        path="building-details/:buildingId"
        element={
          <Suspense fallback={<Loader />}>
            <BuildingDetailsPage />
          </Suspense>
        }
      />
      <Route
        path="partialdetails"
        element={
          <Suspense fallback={<Loader />}>
            <PartialDetails />
          </Suspense>
        }
      />
      <Route
        path="detailedoverview"
        element={
          <Suspense fallback={<Loader />}>
            <DetailedOverview />
          </Suspense>
        }
      />

      {ReportsRoutes()}
    </Route>
  );
}

export function DashboardRoutes(t) {
  return (
    <Route
      path="/dashboard"
      element={
        <RouteErrorBoundary
          title="Dashboard section crashed"
          message="A rendering error was contained to this workspace section. You can retry this section without reloading the whole app."
          showReload={false}
        >
          <NotificationProvider>
            <PrivateRoutes />
          </NotificationProvider>
        </RouteErrorBoundary>
      }
    >
      {CustomerBuildingRoutes()}
      {RoleBasedRoutes(t)}
      {/* Rendered outside RoleBasedLayout so the denied page carries no role
          sidebar/chrome. Reached both by the axios 403 interceptor and by
          role route guards. */}
      <Route path="access-denied" element={<AccessDenied />} />
      <Route
        path="annotation-tool"
        element={
          <Suspense fallback={<Loader />}>
            <ProtectedRoute>
              <RouteErrorBoundary
                title="Annotation tool crashed"
                message="An annotation render error was contained to this page. Your session is still active and the rest of the app remains available."
                showReload={false}
              >
                <AnnotationTool />
              </RouteErrorBoundary>
            </ProtectedRoute>
          </Suspense>
        }
      />
    </Route>
  );
}

export { NotFoundPage };
